// /src/utils/sessionCompletion.js
// Shared helpers for recording a training session completion.
// Used by the full session player and by the "Mark as Complete" shortcut
// on the session overview so both paths update acl_care the same way.

export const SESSION_STORAGE_KEY = 'acl_care_session_progress';

/**
 * Return a new acl_care object with the completion count for the given
 * phase/session incremented by one.
 */
export function incrementSessionCompletion(aclCare, phaseNum, workoutNum) {
  const phaseKey = `phase_${phaseNum}`;
  const sessionKey = `session_${workoutNum}`;
  const currentCompletions = aclCare?.phases?.[phaseKey]?.completions?.[sessionKey] || 0;

  return {
    ...aclCare,
    phases: {
      ...(aclCare?.phases || {}),
      [phaseKey]: {
        ...(aclCare?.phases?.[phaseKey] || {}),
        completions: {
          ...(aclCare?.phases?.[phaseKey]?.completions || {}),
          [sessionKey]: currentCompletions + 1,
        },
      },
    },
    updated_at: new Date().toISOString(),
  };
}

/**
 * Remove any in-progress session from local storage if it belongs to the
 * given phase/session (or unconditionally when no phase/session is given).
 */
export function clearStoredSession(phaseNum, workoutNum) {
  const stored = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!stored) return;

  if (phaseNum == null || workoutNum == null) {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }

  try {
    const parsed = JSON.parse(stored);
    if (parsed.phaseNum === phaseNum && parsed.workoutNum === workoutNum) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  }
}

/**
 * Build a training-session payload for a workout the user marked complete
 * from the overview screen without stepping through each exercise.
 * Mirrors the shape produced by SessionPlayerPage so reporting stays uniform.
 */
export function buildManualCompletionPayload({ userId, phaseNum, workoutNum, supersets }) {
  const now = new Date().toISOString();

  const supersetsData = supersets.map((superset, index) => ({
    superset_id: superset.id,
    sequence: index,
    completed: true,
    exercise1_id: `${superset.id}_ex1`,
    exercise1_name: superset.exercise1.name,
    exercise2_id: `${superset.id}_ex2`,
    exercise2_name: superset.exercise2.name,
    sets: { set1: null, set2: null, set3: null },
  }));

  return {
    user_id: userId,
    phase: phaseNum,
    workout: workoutNum,
    session_type: 'training',
    started_at: now,
    completed_at: now,
    duration_minutes: 0,
    warmup_completed: true,
    exercises: {
      manual_completion: true,
      supersets: supersetsData,
    },
  };
}
